import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  Button,
  Avatar,
  Paper,
} from '@mui/material';
import {
  EmojiEvents,
  Groups,
  Public,
  AutoAwesome,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

const AboutPage: React.FC = () => {
  const theme = useTheme();

  const values = [
    {
      icon: <AutoAwesome />,
      title: 'Simple by Design',
      description: 'We believe powerful tools shouldn\'t be complex. Every feature is designed to be intuitive and immediately useful.',
    },
    {
      icon: <Groups />,
      title: 'Customer Success',
      description: 'Your team\'s productivity drives everything we do. We build features that solve real content-sharing challenges.',
    },
    {
      icon: <Public />,
      title: 'Fair Pricing',
      description: 'Revolutionary concurrent user pricing means teams of all sizes can afford enterprise-grade content sharing.',
    },
    {
      icon: <EmojiEvents />,
      title: 'Continuous Improvement',
      description: 'We listen to feedback and continuously refine the platform to better serve your content-sharing needs.',
    },
  ];


  const stats = [
    { value: '80-90%', label: 'Cost Reduction' },
    { value: '10:1', label: 'User to Seat Ratio' },
    { value: 'Simple', label: 'Administration' },
    { value: 'All Types', label: 'Content Supported' },
  ];

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
          pt: { xs: 6, md: 10 },
          pb: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 800,
                mb: 3,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Making Content Sharing Simple and Affordable
            </Typography>
            <Typography variant="h5" color="text.secondary" sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
              We're on a mission to provide teams with powerful, intuitive content-sharing tools at a fraction of traditional costs
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ mt: -6, position: 'relative', zIndex: 1 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Grid container spacing={3}>
            {stats.map((stat) => (
              <Grid item xs={6} md={3} key={stat.label}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>

      {/* Story Section */}
      <Container maxWidth="md" sx={{ py: 10 }}>
        <Typography variant="h3" sx={{ mb: 4, fontWeight: 700, textAlign: 'center' }}>
          Our Story
        </Typography>
        <Typography variant="body1" paragraph sx={{ fontSize: '1.125rem', lineHeight: 1.8 }}>
          Tier One Content was born from a simple observation: content sharing shouldn't be complicated or expensive.
          Traditional platforms force teams into expensive per-seat licensing models and use rigid folder structures
          that don't match how teams actually work.
        </Typography>
        <Typography variant="body1" paragraph sx={{ fontSize: '1.125rem', lineHeight: 1.8 }}>
          We took a different approach. Our revolutionary concurrent user pricing model means teams pay only for
          simultaneous active users—not every team member who might occasionally need access. This single innovation
          reduces costs by 80-90% compared to traditional licensing while serving more users.
        </Typography>
        <Typography variant="body1" paragraph sx={{ fontSize: '1.125rem', lineHeight: 1.8 }}>
          But cost efficiency was only part of the solution. We built powerful multi-dimensional tagging to replace
          limiting folder hierarchies, comprehensive search across all content attributes, curated View pages for
          organized content presentation, and true multi-company team support—all wrapped in an interface so
          intuitive that teams are productive from day one.
        </Typography>
        <Typography variant="body1" sx={{ fontSize: '1.125rem', lineHeight: 1.8 }}>
          Today, teams worldwide use Tier One Content to share everything from product documentation to marketing
          materials to training resources. Whether you're a small startup or managing content across multiple
          organizations, our platform grows with you—simply, affordably, and powerfully.
        </Typography>
      </Container>

      {/* Values Section */}
      <Box sx={{ backgroundColor: 'background.default', py: 10 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ mb: 6, fontWeight: 700, textAlign: 'center' }}>
            Our Values
          </Typography>
          <Grid container spacing={4}>
            {values.map((value) => (
              <Grid item xs={12} md={6} key={value.title}>
                <Card sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: `${theme.palette.primary.main}15`,
                          color: 'primary.main',
                          width: 56,
                          height: 56,
                          mr: 2,
                        }}
                      >
                        {value.icon}
                      </Avatar>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                          {value.title}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {value.description}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          py: 8,
          color: 'white',
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" sx={{ mb: 3, fontWeight: 700 }}>
            Experience Simple, Affordable Content Sharing
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.95 }}>
            Join teams saving 80-90% on content sharing costs
          </Typography>
          <Button
            component={Link}
            to="/signup"
            variant="contained"
            size="large"
            sx={{
              py: 1.5,
              px: 4,
              background: '#ffffff',
              color: theme.palette.primary.dark,
              fontWeight: 700,
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.92)',
              },
            }}
          >
            Start Free Forever
          </Button>
        </Container>
      </Box>
    </>
  );
};

export default AboutPage;